import React from "react";
import { CardContainer } from "@swiggy-private/dls-web-core/dist/components/CardContainer";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import OTPInputBox from "@swiggy-private/dls-web-recipe-food-web/dist/components/OTPInputBox";
import { DLS_TEXT_VARIANTS } from "constants/dls";
import {
    HeroSection as HeroSectionStyled,
    SafetyBadge,
    GuardIcon,
    FormContent,
    InputWrapper,
    TermsWrapper,
    HeroSectionCardContainer,
    StyledFullWidthButton,
} from "./styles";
import { Flex } from "@swiggy-private/dls-web-layouts/dist/components/Flex";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { Svg12 as ModifyIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/modify";
import { logTermsAndConditionsClick } from "./analytics";

interface HeroSectionProps {
    phoneNumber: string;
    onPhoneChange: (value: string) => void;
    onPhoneBlur?: () => void;
    onGetStarted: () => void;
    isButtonDisabled: boolean;
    showOTP: boolean;
    otpValue: string;
    onOTPChange: (value: string) => void;
    onResendOTP: () => void;
    onContinue: () => void;
    isOTPButtonDisabled: boolean;
    resendTimer: number;
    canResend: boolean;
    formatTime: (seconds: number) => string;
    error: string | null;
    sendOTPError: string | null;
    onModifyPhone: () => void;
    totalAttemptsRemaining: number;
    isInCooldown: boolean;
    cooldownTimeRemaining: number;
    formatCooldownTime: (seconds: number) => string;
}

export const HeroSection = ({
    phoneNumber,
    onPhoneChange,
    onPhoneBlur,
    onGetStarted,
    isButtonDisabled,
    showOTP,
    otpValue,
    onOTPChange,
    onResendOTP,
    onContinue,
    isOTPButtonDisabled,
    resendTimer,
    canResend,
    formatTime,
    error,
    sendOTPError,
    onModifyPhone,
    isInCooldown,
    cooldownTimeRemaining,
    formatCooldownTime,
}: HeroSectionProps) => {
    const handleTermsClick = () => {
        logTermsAndConditionsClick();
        // Open the Terms & Conditions PDF in a new tab
        window.open("/docs/terms_and_conditions.pdf", "_blank");
    };

    const handleOTPChange = (value: string) => {
        // Only allow numeric characters
        const numericValue = value.replace(/[^0-9]/g, "");
        onOTPChange(numericValue);
    };
    return (
        <HeroSectionStyled>
            <div style={{ width: "100%" }}>
                <HeroSectionCardContainer cornerRadius="large" stroke="border_Tertiary" elevation>
                    <SafetyBadge>
                        <GuardIcon as="img" src="/images/guard-icon.svg" alt="" />
                        <Text variant={DLS_TEXT_VARIANTS.BODY_B2_MED}>100% Safe & Secure</Text>
                    </SafetyBadge>

                    <FormContent>
                        {!showOTP ? (
                            <>
                                <InputWrapper>
                                    <TextField
                                        textFieldDetails={{
                                            value: phoneNumber,
                                            label: "Your phone number",
                                            name: "phone",
                                            type: TextFieldTypes.PHONE,
                                            onChange: onPhoneChange,
                                            onBlur: onPhoneBlur,
                                            inputLimit: 10,
                                            errorText: sendOTPError || "",
                                        }}
                                    />
                                </InputWrapper>

                                <div style={{ width: "100%" }}>
                                    <StyledFullWidthButton
                                        variant="primary"
                                        onClick={onGetStarted}
                                        isDisabled={isButtonDisabled}
                                    >
                                        Get Started
                                    </StyledFullWidthButton>
                                </div>
                            </>
                        ) : (
                            <>
                                <Flex justifyContent="center" alignItems="center" width="100%" gap={4}>
                                    <Text variant="V2.body1.neutral.medium" color="text_High_Emphasis">
                                        Enter the OTP sent to{" "}
                                        <span
                                            data-x-apple-data-detectors="none"
                                            data-x-apple-data-detectors-type="none"
                                        >
                                            {phoneNumber}
                                        </span>
                                    </Text>
                                    <GenericIcon
                                        ariaLabel="Modify Phone Number"
                                        Icon={ModifyIcon}
                                        strokeColor={"text_Highest_Emphasis"}
                                        fillColor="primary"
                                        onClick={onModifyPhone}
                                        style={{ cursor: "pointer" }}
                                    />
                                </Flex>

                                <InputWrapper isOTPInput>
                                    <OTPInputBox
                                        value={otpValue}
                                        onChange={handleOTPChange}
                                        length={6}
                                        errorMsg={isInCooldown ? "OTP attempts limit reached" : error!}
                                    />
                                </InputWrapper>

                                <Flex justifyContent="center" alignItems="center" gap={8} width="100%">
                                    <Text variant="V2.body1.neutral.medium" color="text_Med_Emphasis">
                                        Did not get OTP?{" "}
                                    </Text>
                                    {canResend && !isInCooldown ? (
                                        <Text
                                            variant="V2.body2.neutral.bold"
                                            color="primary"
                                            onClick={onResendOTP}
                                            style={{ cursor: "pointer" }}
                                        >
                                            Resend
                                        </Text>
                                    ) : (
                                        <Text variant="V2.body2.neutral.bold" color="text_Med_Emphasis">
                                            {isInCooldown
                                                ? `Resend OTP in ${formatCooldownTime(cooldownTimeRemaining)}`
                                                : `Resend OTP in ${formatTime(resendTimer)}`}
                                        </Text>
                                    )}
                                </Flex>

                                <div style={{ width: "100%" }}>
                                    <StyledFullWidthButton
                                        variant="primary"
                                        onClick={onContinue}
                                        isDisabled={isOTPButtonDisabled}
                                    >
                                        Continue
                                    </StyledFullWidthButton>
                                </div>
                            </>
                        )}

                        <TermsWrapper>
                            <Text variant="V2.overline.neutral.medium" style={{ color: "#66686E", fontSize: "12px" }}>
                                By clicking this button, you accept our
                            </Text>
                            <Text
                                variant="V2.overline.neutral.bold"
                                style={{
                                    color: "#FF5200",
                                    fontSize: "12px",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    whiteSpace: "nowrap",
                                }}
                                onClick={handleTermsClick}
                            >
                                Terms & Conditions
                            </Text>
                        </TermsWrapper>
                    </FormContent>
                </HeroSectionCardContainer>
            </div>
        </HeroSectionStyled>
    );
};
